/**
 * IconSpriteDefs — the <svg><defs> block itself, mounted once per document by
 * app/layout.tsx through the `IconSprite` re-export in ./icon-sprite.tsx.
 *
 * This module is the ONLY client module of the sprite family. The icon
 * INSTANCES (`StarIcon`, `ArrowRightIcon`, …) stay in ./icon-sprite.tsx, a
 * plain module, so a server component rendering five hundred stars keeps
 * rendering five hundred 40-byte <use> elements on the server — a client
 * instance would put a client-reference row per star back into the RSC
 * payload, the exact FRIDGE-179 shape this sprite exists to remove.
 *
 * WHY THIS IS A CLIENT COMPONENT (FRIDGE-929, measured 09/17/2026)
 * ----------------------------------------------------------------
 * The sprite is still server-rendered — a client component is SSR'd into the
 * HTML in place, first in <body>, exactly as before (tests/icon-sprite-repeats
 * .test.ts renders it with react-dom/server). What changes is the React Server
 * Components flight payload: a SERVER component's rendered tree is serialised
 * into the `self.__next_f.push` stream for hydration, which put a second,
 * JSON-escaped copy of every <symbol> path into EVERY page — 5,560 bytes per
 * document on production. A client component ships a module reference there
 * instead; the ~4 KB of path data moves into one cached JavaScript chunk that
 * every route already loads.
 */
'use client'

import { STAR_SYMBOL_ID, ARROW_UP_RIGHT_SYMBOL_ID, CHEVRON_DOWN_SYMBOL_ID, ARROW_RIGHT_SYMBOL_ID, CHEVRON_RIGHT_SYMBOL_ID, CHEVRON_LEFT_SYMBOL_ID, COPY_SYMBOL_ID, CALENDAR_SYMBOL_ID, MAIL_SYMBOL_ID, EXTERNAL_LINK_SYMBOL_ID, GLOBE_SYMBOL_ID, SHOPPING_BAG_SYMBOL_ID, LINK_2_SYMBOL_ID, HEART_SYMBOL_ID, HEART_FILLED_SYMBOL_ID, CHECK_SYMBOL_ID, SPRITE_SYMBOL_IDS } from './icon-symbol-ids'

/**
 * FRIDGE-745 (09/04/2026) — the second round of the same shape. Crawler-Eye
 * (CI artifacts, production) served /press/rebate-desert-states-2026 at
 * 290,068 bytes against a 290,000 budget on 09/02, and /brands/lg at
 * 485,635 on 08/31 (then-budget 480,000) and 483,531 on 09/03 against
 * today's 486,000 — 2.5 KB of headroom. Inventory of the served markup,
 * per lucide glyph, full inline copy each time:
 *   /brands/lg   arrow-right x20 (7.3 KB) · chevron-down x13 (4.3 KB — the
 *                lucide one, NOT the sprite above) · chevron-right x7 ·
 *                chevron-left x6
 *   the release  arrow-right x7 · copy x6 (one per citation) · shopping-bag,
 *                globe, calendar, mail x3 each · link-2, external-link x2
 * Ten glyphs, each 280–480 bytes per copy, each drawn once here and ~110
 * bytes per <use>. Geometry is lucide's own, copied from the served markup so
 * every instance renders pixel-identically; stroke attributes live on the
 * symbol, colour still comes from the caller (currentColor), size from the
 * caller's classes exactly as with lucide (width/height 24 attributes, CSS
 * wins). Budgets untouched.
 */

/** lucide's stroke defaults, once per symbol instead of once per instance. */
const STROKE = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const

export function IconSprite() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={0}
      height={0}
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
    >
      <defs>
        {/* Star rating glyph. viewBox matches every previous inline copy, so
            instances render pixel-identically at any size. */}
        <symbol id={STAR_SYMBOL_ID} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </symbol>
        {/* The catalog-card arrow (lucide arrow-up-right geometry). Stroke
            attributes live HERE so each instance is a bare <svg><use/> —
            /brands/lg carried 59 full inline copies of this glyph, the exact
            FRIDGE-179 shape on a second icon. Colour still inherits from the
            caller's text class via currentColor. */}
        <symbol id={ARROW_UP_RIGHT_SYMBOL_ID} viewBox="0 0 24 24">
          <g fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 7h10v10" />
            <path d="M7 17 17 7" />
          </g>
        </symbol>
        {/* The accordion chevron (lucide chevron-down geometry). The press FAQ
            section renders one per question — 15 full inline copies (~4.7 KB)
            on a release, found while measuring the /press byte-budget breach
            on 08/30/2026. Same discipline as the arrow: stroke attributes on
            the symbol, currentColor from the caller. */}
        <symbol id={CHEVRON_DOWN_SYMBOL_ID} viewBox="0 0 24 24">
          <path
            d="m6 9 6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </symbol>
        {/* FRIDGE-745 — the ten lucide glyphs that repeat per item on the
            brand page and the press release (see the header note). */}
        {/* 09/06/2026 — the Save heart and the Compare tick the header
            Saved/Compare work put on every catalog card. Inline, each card
            carried two full stroke paths; /brands/lg gained 65 KB of server
            HTML and the html-render gate went red on four page types — the
            FRIDGE-179 shape again. Two heart symbols (outline, filled) so the
            saved state needs no per-instance fill; colour still inherits. */}
        <symbol id={HEART_SYMBOL_ID} viewBox="0 0 24 24">
          <path d="M12 20.5S4 15 4 9.6A4.4 4.4 0 0112 6a4.4 4.4 0 018 3.6C20 15 12 20.5 12 20.5z" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
        </symbol>
        <symbol id={HEART_FILLED_SYMBOL_ID} viewBox="0 0 24 24">
          <path d="M12 20.5S4 15 4 9.6A4.4 4.4 0 0112 6a4.4 4.4 0 018 3.6C20 15 12 20.5 12 20.5z" fill="currentColor" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
        </symbol>
        <symbol id={CHECK_SYMBOL_ID} viewBox="0 0 24 24">
          <path d="M4 12.5l5 5L20 6.5" fill="none" stroke="#ffffff" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
        </symbol>
        <symbol id={ARROW_RIGHT_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </g>
        </symbol>
        <symbol id={CHEVRON_RIGHT_SYMBOL_ID} viewBox="0 0 24 24">
          <path d="m9 18 6-6-6-6" {...STROKE} />
        </symbol>
        <symbol id={CHEVRON_LEFT_SYMBOL_ID} viewBox="0 0 24 24">
          <path d="m15 18-6-6 6-6" {...STROKE} />
        </symbol>
        <symbol id={COPY_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </g>
        </symbol>
        <symbol id={CALENDAR_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="M8 2v3" />
            <path d="M16 2v3" />
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
          </g>
        </symbol>
        <symbol id={MAIL_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
            <rect x="2" y="4" width="20" height="16" rx="2" />
          </g>
        </symbol>
        <symbol id={EXTERNAL_LINK_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          </g>
        </symbol>
        <symbol id={GLOBE_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
          </g>
        </symbol>
        <symbol id={SHOPPING_BAG_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="M16 10a4 4 0 0 1-8 0" />
            <path d="M3.103 6.034h17.794" />
            <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
          </g>
        </symbol>
        <symbol id={LINK_2_SYMBOL_ID} viewBox="0 0 24 24">
          <g {...STROKE}>
            <path d="M9 17H7A5 5 0 0 1 7 7h2" />
            <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
            <line x1="8" x2="16" y1="12" y2="12" />
          </g>
        </symbol>
      </defs>
    </svg>
  )
}
